/* iter-training-v-ach-coach — Achievement badge chip.
 *
 * Renders a compact trophy chip listing awarded achievements. Two
 * sizes: `sm` for inline placement on book cards, `md` for panels.
 * The trophy list is horizontal-scrollable when it overflows.
 */
import React from 'react';
import { Trophy, GraduationCap, Sparkles } from 'lucide-react';

const KIND_META = {
  training:  { icon: GraduationCap, tint: 'bg-emerald-500/15 text-emerald-800 dark:text-emerald-200 border-emerald-500/40' },
  milestone: { icon: Trophy,        tint: 'bg-amber-500/15  text-amber-800   dark:text-amber-200   border-amber-500/40'  },
  other:     { icon: Sparkles,      tint: 'bg-primary/10    text-primary                             border-primary/40'    },
};

export function AchievementChip({ meta, size = 'sm', testId }) {
  const kind  = meta?.kind || 'other';
  const style = KIND_META[kind] || KIND_META.other;
  const Icon  = style.icon;
  const s = size === 'sm'
    ? 'h-4 gap-0.5 rounded-sm border px-1 text-[9px]'
    : 'h-5 gap-1   rounded    border px-1.5 text-[10.5px]';
  return (
    <span
      className={`inline-flex items-center whitespace-nowrap font-medium ${style.tint} ${s}`}
      data-testid={testId}
      title={meta?.title || 'Achievement'}
    >
      <Icon className={size === 'sm' ? 'h-2.5 w-2.5' : 'h-3 w-3'} />
      {meta?.title || 'Awarded'}
    </span>
  );
}

export default function AchievementList({ items = [], size = 'sm', testId, max = 4 }) {
  if (!items || items.length === 0) return null;
  const shown = items.slice(0, max);
  const extra = items.length - shown.length;
  return (
    <div
      className="flex flex-wrap items-center gap-0.5"
      data-testid={testId || 'achievement-list'}
    >
      {shown.map((it) => (
        <AchievementChip
          key={it.key || it.title}
          meta={it.meta || it}
          size={size}
          testId={`ach-chip-${(it.key || it.title || '').replace(/[^a-z0-9-]/gi, '-')}`}
        />
      ))}
      {extra > 0 && (
        <span
          className="rounded-sm border border-border bg-muted px-1 text-[9px] text-muted-foreground"
          data-testid="ach-chip-more"
        >
          +{extra}
        </span>
      )}
    </div>
  );
}
